/**
 * `useErrorToasts()` — a central place to translate API errors to toasts and
 * navigation. Hook into mutation `onError` or query `onError` callbacks.
 *
 * Per UX-AC-081 / AC-030: any 403 from a household-scoped endpoint routes the
 * user to /home of the active household with the toast "That item isn't in
 * this household." No raw status code or foreign id is surfaced.
 */
import { useNavigate } from 'react-router-dom';
import { useToast } from './store';
import { ApiError } from './api';

const FOREIGN_HOUSEHOLD_MESSAGE = "That item isn't in this household.";
const SESSION_EXPIRED_MESSAGE = 'Your session expired. Send a new link.';

export function useErrorToasts() {
  const toast = useToast();
  const navigate = useNavigate();
  return {
    /**
     * Call from a mutation/query onError handler. Returns true if it surfaced
     * an editorial toast/redirect, false if the caller should fall back to its
     * own messaging.
     */
    handle(err: unknown): boolean {
      if (!(err instanceof ApiError)) return false;
      // Cross-household — route to home + neutral toast.
      if (
        err.status === 403 ||
        err.code === 'FORBIDDEN' ||
        err.code === 'NOT_FOUND' ||
        err.code === 'PLACE_NOT_FOUND' ||
        err.code === 'CATEGORY_NOT_FOUND'
      ) {
        toast.error(FOREIGN_HOUSEHOLD_MESSAGE);
        // Only navigate when we're not already on /home — avoid loops.
        if (
          typeof window !== 'undefined' &&
          window.location.pathname !== '/'
        ) {
          navigate('/', { replace: true });
        }
        return true;
      }
      if (err.status === 401 || err.code === 'UNAUTHENTICATED') {
        // The auth guard will route; surface the banner-carrying query param.
        if (typeof window !== 'undefined') {
          window.location.assign('/auth/request?session=expired');
        }
        toast.error(SESSION_EXPIRED_MESSAGE);
        return true;
      }
      if (err.status === 0 || err.code === 'INTERNAL') {
        toast.error("Couldn't reach the server. Try again.");
        return true;
      }
      // Other 4xx — surface the editorial server message if present.
      toast.error(err.message);
      return true;
    },
  };
}
